import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { X } from "lucide-react";

const STORAGE_KEY = "revgrow.consent";

// Soft cookie/analytics consent banner. Not a blocking cookie wall —
// PostHog runs with person_profiles=identified_only and we don't set any
// advertising cookies. This banner exists purely as a courtesy/legal signal
// to DACH visitors and to surface the Impressum + Datenschutz links.
export default function ConsentBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Small delay so the banner doesn't fight the hero animation on first paint.
    const already = typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY);
    if (already) return;
    const timer = setTimeout(() => setVisible(true), 900);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ accepted: true, at: Date.now() }));
    } catch (_) {
      /* ignore */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed z-[60] inset-x-4 bottom-4 md:inset-x-auto md:right-6 md:bottom-6 md:max-w-md animate-fade-up"
      data-testid="consent-banner"
      role="dialog"
      aria-live="polite"
      aria-label={t("consent.title")}
    >
      <div className="relative rounded-lg border border-white/10 bg-ink-800/95 backdrop-blur-xl p-6 shadow-2xl">
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-bone-200/50 hover:text-bone-100 transition-colors"
          aria-label="Close"
          data-testid="consent-close"
        >
          <X size={16} />
        </button>

        <p className="text-xs uppercase tracking-widest2 text-clay-400 mb-3">
          {t("consent.title")}
        </p>
        <p className="text-sm text-bone-200/85 leading-relaxed mb-6">
          <Trans
            i18nKey="consent.body"
            components={{
              1: (
                <Link
                  to="/privacy"
                  className="text-clay-400 hover:underline underline-offset-2"
                  data-testid="consent-link-privacy"
                />
              ),
              3: (
                <Link
                  to="/impressum"
                  className="text-clay-400 hover:underline underline-offset-2"
                  data-testid="consent-link-impressum"
                />
              ),
            }}
          />
        </p>

        <div className="flex flex-wrap items-center gap-4">
          <button
            onClick={dismiss}
            className="btn-primary text-xs"
            data-testid="consent-accept"
          >
            {t("consent.accept")}
          </button>
          <Link
            to="/privacy"
            onClick={dismiss}
            className="text-xs uppercase tracking-widest2 text-bone-200/70 hover:text-clay-400 transition-colors"
            data-testid="consent-learn-more"
          >
            {t("consent.learnMore")}
          </Link>
        </div>
      </div>
    </div>
  );
}
